import React from 'react';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import { Button } from '@project-stories/elements/Button/Button';
import useCurrentTextStyle from '../../../../hooks/useCurrentTextStyle';
import { ObviyoProductRecommendations } from '@src/stories/blocks/ObviyoRecommendations/ObviyoProductRecommendations';
import CarouselArrows from '@src/stories/elements/CarouselArrows/CarouselArrows';

interface MinicartEmptyStateProps {
  block?: any;
  blocks?: any;
  settings: {
    // Content settings
    empty_text_one?: string;
    empty_text_one_text_style?: string;
    empty_text_one_style_enable_responsive?: boolean;
    empty_text_one_style_mobile?: string;
    empty_text_one_style_tablet?: string;
    empty_text_one_style_ultrawide?: string;
    empty_text_two?: string;
    empty_text_two_text_style?: string;
    empty_text_two_style_enable_responsive?: boolean;
    empty_text_two_style_mobile?: string;
    empty_text_two_style_tablet?: string;
    empty_text_two_style_ultrawide?: string;
    empty_button_text?: string;
    empty_button_style?: string;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;

    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_bottom?: number;
    padding_left_right?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;

    // Legacy settings (for backward compatibility)
    empty_text_one_style?: string;
    empty_text_two_style?: string;

    // Obviyo recommendations settings
    recommendation_id?: string;
    max_results?: number;
    color_schema_obviyo?: string;
    title_obviyo?: string;
    title_obviyo_style?: string;
    title_obviyo_style_enable_responsive?: boolean;
    title_obviyo_style_mobile?: string;
    title_obviyo_style_tablet?: string;
    title_obviyo_style_ultrawide?: string;
  };
  onClose: () => void;
  showObviyoRecommendations?: boolean;
}

export const MinicartEmptyState = ({
  settings,
  block,
  blocks,
  onClose,
  showObviyoRecommendations = true,
}: MinicartEmptyStateProps) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const splideRef = useRef<any>(null);

  const [hasRecommendations, setHasRecommendations] = useState(false);

  // Content settings with responsive support
  const textOne = settings?.empty_text_one || 'Your bag is empty';
  const baseTextOneStyle = settings?.empty_text_one_text_style || settings?.empty_text_one_style;
  const textOneTextStyle = getCurrentTextStyle(
    baseTextOneStyle,
    settings?.empty_text_one_style_enable_responsive,
    settings?.empty_text_one_style_mobile,
    settings?.empty_text_one_style_tablet,
    settings?.empty_text_one_style_ultrawide
  );

  const textTwo = settings?.empty_text_two || '';
  const baseTextTwoStyle = settings?.empty_text_two_text_style || settings?.empty_text_two_style;
  const textTwoTextStyle = getCurrentTextStyle(
    baseTextTwoStyle,
    settings?.empty_text_two_style_enable_responsive,
    settings?.empty_text_two_style_mobile,
    settings?.empty_text_two_style_tablet,
    settings?.empty_text_two_style_ultrawide
  );

  const buttonText = settings?.empty_button_text || 'Start Shopping';
  const buttonStyle = settings?.empty_button_style || 'primary';
  const viewBagText = 'View Bag';

  // Layout & Spacing
  const direction = settings?.direction || 'column';
  const paddingTop = settings?.padding_top ?? 20;
  const paddingBottom = settings?.padding_bottom ?? 20;
  const paddingLeftRight = settings?.padding_left_right ?? 20;
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 0;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  // Legacy support - removed unused padding setting

  const handleContinueShopping = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
    onClose();
  };

  const handleViewBag = () => {
    window.dispatchEvent(new CustomEvent('cart:open'));
  };

  // Container styles
  const containerStyle = {
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    borderRadius: `${borderRadius}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'center',
    gap: '12px',
    minHeight: '350px',
    position: 'relative'
  } as React.CSSProperties;

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <div
      className={`minicart-empty-state h-full ${colorSchema ? `color-${colorSchema}` : ''}`}
      style={containerStyle}
    >
      <div className='flex flex-col items-center w-full grow justify-between gap-4'>
        <div class='h-full w-full'>
          <div className='empty-cart-icon mb-6 animate-bounceIn hidden'>
            <svg
              width='64'
              height='64'
              viewBox='0 0 64 64'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='opacity-60'
            >
              <path
                d='M20 20V16C20 11.6 23.6 8 28 8H36C40.4 8 44 11.6 44 16V20'
                stroke='currentColor'
                strokeWidth='2'
                strokeMiterlimit='10'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M52 24V56H12V24H52Z'
                stroke='currentColor'
                strokeWidth='2'
                strokeMiterlimit='10'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='animate-slideInUp stagger-1 bg-[#EDEDED] w-full px-[16px] py-[12px] rounded-[8px] text-left'>
            <Paragraph
              classes={textOneTextStyle}
              text={textOne}
            />
          </div>
          {textTwo && (
            <div className='animate-slideInUp stagger-2 text-left'>
              <Paragraph
                classes={textTwoTextStyle}
                text={textTwo}
              />
            </div>
          )}
        </div>
        {showObviyoRecommendations && (
          <div className={`product-obviyo-recommendations w-full`}>
            {hasRecommendations && (
              <div
                className={`mb-1 py-2.5 px-4 flex items-center justify-between rounded-t-2xl color-${
                  block?.color_schemas?.color_schema_obviyo || 'default'
                }`}
              >
                <span
                  className={getCurrentTextStyle(
                    settings?.title_obviyo_style || 'b9--bold',
                    settings?.title_obviyo_style_enable_responsive,
                    settings?.title_obviyo_style_mobile,
                    settings?.title_obviyo_style_tablet,
                    settings?.title_obviyo_style_ultrawide
                  )}
                >
                  {settings?.title_obviyo || 'Recommended'}
                </span>
                <CarouselArrows splideRef={splideRef} />
              </div>
            )}
            <ObviyoProductRecommendations
              settings={settings}
              ref={splideRef}
              onRecommendationsLoaded={(loaded) => setHasRecommendations(loaded)}
              type='cart'
            />
          </div>
        )}
      </div>
      {/* Divider if needed */}
      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
      {/* Bottom buttons */}
      <div className='flex flex-col items-center w-full gap-2 mt-8 mb-2'>
        <Button
          style={buttonStyle}
          className='w-full rounded-full py-3 text-base font-semibold bg-black text-white hover:bg-gray-900 transition-all duration-150'
          label={buttonText}
          onClick={handleContinueShopping}
        />
        <button
          className='w-full text-center underline text-base font-semibold text-black py-2 bg-transparent hover:text-gray-700 transition-all duration-150'
          onClick={handleViewBag}
        >
          {viewBagText}
        </button>
      </div>
    </div>
  );
};
